import React, { useState } from 'react';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Sparkles, Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours.",
    });
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-purple-600 via-purple-700 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="text-sm text-white/90">Get in Touch</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-extralight mb-6 tracking-tight">
            Let's Create
            <br />
            <span className="text-purple-200">The Future Together</span>
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
            Have a question or want to discuss a project? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-3xl text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-light mb-2">Email</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-purple-600 hover:text-purple-700">
                {companyInfo.email}
              </a>
            </div>
            <div className="bg-gray-50 p-8 rounded-3xl text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-light mb-2">Phone</h3>
              <a href={`tel:${companyInfo.phone}`} className="text-purple-600 hover:text-purple-700">
                {companyInfo.phone}
              </a>
            </div>
            <div className="bg-gray-50 p-8 rounded-3xl text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-light mb-2">Location</h3>
              <p className="text-gray-600 text-sm">{companyInfo.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light mb-4 tracking-tight">
              Send Us a <span className="text-purple-600">Message</span>
            </h2>
            <p className="text-lg text-gray-600">
              We'll get back to you within 24 hours
            </p>
          </div>

          <form onSubmit={handleContactSubmit} className="bg-white p-10 rounded-3xl shadow-lg">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="contact-name" className="text-gray-700 mb-2 block">Name *</Label>
                <Input
                  id="contact-name"
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-gray-700 mb-2 block">Email *</Label>
                <Input
                  id="contact-email"
                  type="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="contact-phone" className="text-gray-700 mb-2 block">Phone</Label>
                <Input
                  id="contact-phone"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <Label htmlFor="contact-company" className="text-gray-700 mb-2 block">Company</Label>
                <Input
                  id="contact-company"
                  type="text"
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600"
                  placeholder="Your company"
                />
              </div>
            </div>

            <div className="mb-8">
              <Label htmlFor="contact-message" className="text-gray-700 mb-2 block">Message *</Label>
              <Textarea
                id="contact-message"
                required
                rows={6}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="rounded-xl border-gray-300 focus:border-purple-600 focus:ring-purple-600 resize-none"
                placeholder="Tell us about your project or inquiry..."
              />
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all hover:scale-[1.02]"
            >
              <Send className="mr-2 w-5 h-5" />
              Send Message
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;